import { ActionIcon, Badge, Group, SimpleGrid, Stack, Table, Text } from "@mantine/core";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Fragment, useState } from "react";

import type {
  CallOutcome,
  DeliveryState,
  TraceCost,
  TraceRequest,
  TraceWebhook,
} from "../../api/generated/model";
import { formatDateTime } from "../../ui/format";
import { Facts, JobCost, Unknown, formatDuration, formatUsd } from "./usage-format";
import classes from "./usage.module.css";

const OUTCOME: Record<CallOutcome, { label: string; color?: string }> = {
  pending: { label: "Pending" },
  succeeded: { label: "Succeeded", color: "teal" },
  failed: { label: "Failed", color: "red" },
  refused: { label: "Refused", color: "red" },
  unavailable: { label: "Unavailable", color: "orange" },
  not_sent: { label: "Not sent", color: "gray" },
  ambiguous: { label: "Ambiguous", color: "orange" },
  canceled: { label: "Canceled", color: "gray" },
};

const SOURCE = {
  table: "the price table",
  free: "free: the vendor does not bill it",
  vendor: "the vendor's bill",
} as const;

const at = (iso: string | null) => (iso === null ? null : Date.parse(iso));
const when = (iso: string | null) => (iso === null ? "—" : formatDateTime(iso));
/** The request's last moment known: its end, its last status read, its answer, or its send. */
const lastOf = (request: TraceRequest) =>
  at(request.finished_at) ?? at(request.last_polled_at) ?? at(request.answered_at) ?? Date.parse(request.requested_at);

/** Which outputs a request made: the job's own call (none), one output, or a run of them. */
function outputsOf({ output_first: first, output_count: count }: TraceRequest): string {
  if (first === null) return "job";
  return count === null || count <= 1 ? `#${first}` : `#${first}–${first + count - 1}`;
}

/**
 * A job's requests at its vendors in order (spec usage-analytics 3): FAL's request id, the
 * endpoint, the outcome and its status reads, the units and their cost, and a bar on the job's
 * own timeline. Each opens to the request as sent and the answer as kept, redacted at write.
 */
export function TraceRequests({ requests, cost }: { requests: TraceRequest[]; cost: TraceCost }) {
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  if (requests.length === 0) {
    return (
      <Text size="sm" c="var(--cai-color-text-muted)">
        None recorded: a run sends none of its own (each step's are in its own trace), and a
        vendor whose adapter does not record yet shows none.
      </Text>
    );
  }
  const start = Math.min(...requests.map((request) => Date.parse(request.requested_at)));
  const span = Math.max(1000, Math.max(...requests.map(lastOf)) - start);
  const toggle = (id: string) =>
    setOpen((shown) => {
      const next = new Set(shown);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  return (
    <Stack gap="xs">
      <Table.ScrollContainer minWidth={1100}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>#</Table.Th>
              <Table.Th>Operation</Table.Th>
              <Table.Th>Vendor · endpoint</Table.Th>
              <Table.Th>Request id</Table.Th>
              <Table.Th className={classes.num}>Output</Table.Th>
              <Table.Th className={classes.status}>Outcome</Table.Th>
              <Table.Th className={classes.num}>Status reads</Table.Th>
              <Table.Th>Units</Table.Th>
              <Table.Th className={classes.num}>Cost</Table.Th>
              <Table.Th miw={180}>
                Timeline
                <Group justify="space-between" fw={500} fz="xs" c="var(--cai-color-text-muted)">
                  <span>0 s</span>
                  <span>{formatDuration(span)}</span>
                </Group>
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {requests.map((request, index) => {
              const shown = open.has(request.id);
              const { label, color } = OUTCOME[request.outcome];
              const sent = Date.parse(request.requested_at);
              const left = ((sent - start) / span) * 100;
              const width = Math.min(100 - left, Math.max(0.8, ((lastOf(request) - sent) / span) * 100));
              return (
                <Fragment key={request.id}>
                  <Table.Tr>
                    <Table.Td className={classes.nowrap}>
                      <ActionIcon
                        variant="subtle"
                        size="sm"
                        aria-expanded={shown}
                        aria-label={`${shown ? "Hide" : "Show"} request ${index + 1}`}
                        onClick={() => toggle(request.id)}
                      >
                        {shown ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </ActionIcon>{" "}
                      {index + 1}
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2} align="flex-start">
                        <Text size="sm">{request.operation}</Text>
                        {request.reconstructed ? (
                          <Badge
                            size="xs"
                            color="gray"
                            variant="light"
                            title="Rebuilt from the job by the backfill: facts only, times approximate"
                          >
                            reconstructed
                          </Badge>
                        ) : null}
                        {request.ai_task === null ? null : (
                          <Text size="xs" c="var(--cai-color-text-muted)">
                            {request.ai_task}
                          </Text>
                        )}
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{request.vendor}</Text>
                      <Text size="xs" ff="monospace" c="var(--cai-color-text-muted)">
                        {request.endpoint ?? "endpoint unknown"}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {request.vendor_ref === null ? (
                        <span className={classes.zero}>—</span>
                      ) : (
                        <code className={classes.ref} title={request.vendor_ref}>
                          {request.vendor_ref}
                        </code>
                      )}
                    </Table.Td>
                    <Table.Td className={classes.num}>{outputsOf(request)}</Table.Td>
                    <Table.Td>
                      <Badge variant="light" {...(color === undefined ? {} : { color })}>
                        {label}
                      </Badge>
                      <Text size="xs" c="var(--cai-color-text-muted)">
                        {request.http_status === null ? "no answer" : `HTTP ${request.http_status}`}
                      </Text>
                    </Table.Td>
                    <Table.Td className={classes.num}>{request.polls}</Table.Td>
                    <Table.Td>
                      {request.units.map((unit) => (
                        <Text key={unit.unit} size="xs" className={classes.nowrap}>
                          {`${unit.unit}: ${unit.quantity}`}
                        </Text>
                      ))}
                    </Table.Td>
                    <Table.Td className={classes.num}>
                      {request.cost_usd === null ? (
                        request.outcome === "pending" ? (
                          "pending"
                        ) : (
                          <Unknown />
                        )
                      ) : (
                        formatUsd(request.cost_usd, true)
                      )}
                      <Text size="xs" c="var(--cai-color-text-muted)">
                        {request.cost_source ?? "no price yet"}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <div
                        className={classes.track}
                        title={`Sent ${when(request.requested_at)}, ${formatDuration(lastOf(request) - sent)} to its last word, ${request.polls} status reads`}
                      >
                        <span style={{ left: `${left}%`, width: `${width}%` }} />
                      </div>
                    </Table.Td>
                  </Table.Tr>
                  {shown ? (
                    <Table.Tr className={classes.detail}>
                      <Table.Td colSpan={10}>
                        <RequestDetail request={request} />
                      </Table.Td>
                    </Table.Tr>
                  ) : null}
                </Fragment>
              );
            })}
          </Table.Tbody>
          <Table.Tfoot>
            <Table.Tr fw={700}>
              <Table.Td colSpan={8}>Known cost of this job</Table.Td>
              <Table.Td className={classes.num}>
                <JobCost usd={cost.usd} complete={cost.complete} open={false} />
              </Table.Td>
              <Table.Td />
            </Table.Tr>
          </Table.Tfoot>
        </Table>
      </Table.ScrollContainer>
      <Text size="xs" c="var(--cai-color-text-muted)">
        A row is a request at the vendor: its status reads and its result fold into it. Open one
        for what was sent and answered.
      </Text>
    </Stack>
  );
}

/** One request's facts, and its body as kept: "expired" past retention, "never recorded" when rebuilt. */
function RequestDetail({ request }: { request: TraceRequest }) {
  const { body } = request;
  const cost =
    request.cost_usd === null
      ? "unknown: no price for this unit yet"
      : `${formatUsd(request.cost_usd, true)}, ${request.cost_source === null ? "" : SOURCE[request.cost_source]}${request.cost_version === null ? "" : `, version ${request.cost_version}`}`;
  return (
    <Stack gap="sm">
      <Facts
        items={[
          ["Requested", when(request.requested_at)],
          ["Answered", when(request.answered_at)],
          ["Finished", when(request.finished_at)],
          ["Last status read", when(request.last_polled_at)],
          ["Attempt", String(request.attempt)],
          ["Vendor's last state", request.vendor_state ?? "—"],
          ["Cost", cost],
          ["Error", request.error_code ?? "—"],
          ["Request id", request.vendor_ref ?? "—"],
          ["Trace id", request.trace_id ?? "—"],
        ]}
      />
      {body === null ? (
        <Group gap="xs">
          <Badge color="gray" variant="light">
            {request.reconstructed ? "never recorded" : request.body_expired ? "expired" : "not kept"}
          </Badge>
          <Text size="sm" c="var(--cai-color-text-muted)">
            {request.reconstructed
              ? "Rebuilt from the job before its requests were recorded: facts only."
              : request.body_expired
                ? "Bodies are kept 90 days."
                : "Its adapter keeps no body yet."}
          </Text>
        </Group>
      ) : (
        <>
          <SimpleGrid cols={{ base: 1, md: 2 }}>
            <Stack gap={4}>
              <Text size="xs" c="var(--cai-color-text-muted)">
                Request, as sent
              </Text>
              <pre className={classes.json}>{JSON.stringify(body.request, null, 2)}</pre>
            </Stack>
            <Stack gap={4}>
              <Text size="xs" c="var(--cai-color-text-muted)">
                Response, its summary
              </Text>
              <pre className={classes.json}>{JSON.stringify(body.response, null, 2)}</pre>
            </Stack>
          </SimpleGrid>
          {body.error_detail === null ? null : (
            <pre className={classes.json}>{body.error_detail}</pre>
          )}
          <Text size="xs" c="var(--cai-color-text-muted)">
            {body.bytes} bytes kept{body.truncated ? ", cut at the cap" : ""}. What the redaction
            put in place reads &quot;[redacted]&quot;, &quot;[webhook]&quot;, or a file by our key;
            never a key, a token or an inline file's bytes.
          </Text>
        </>
      )}
    </Stack>
  );
}

const DELIVERY: Record<DeliveryState, { label: string; color?: string }> = {
  running: { label: "Running" },
  done: { label: "Done", color: "teal" },
  failed: { label: "Failed", color: "red" },
};

/** The verified webhooks the job's vendor sent: which request, what it said, when. */
export function TraceWebhooks({ webhooks }: { webhooks: TraceWebhook[] }) {
  if (webhooks.length === 0) {
    return (
      <Text size="sm" c="var(--cai-color-text-muted)">
        None: only a FAL job of one request takes a webhook; the others are polled.
      </Text>
    );
  }
  return (
    <Table.ScrollContainer minWidth={480}>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Request id</Table.Th>
            <Table.Th>State</Table.Th>
            <Table.Th>Received</Table.Th>
            <Table.Th>Read</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {webhooks.map((webhook) => {
            const { label, color } = DELIVERY[webhook.state];
            return (
              <Table.Tr key={`${webhook.ref} ${webhook.received_at}`}>
                <Table.Td>
                  <code className={classes.ref}>{webhook.ref}</code>
                </Table.Td>
                <Table.Td>
                  <Badge variant="light" {...(color === undefined ? {} : { color })}>
                    {label}
                  </Badge>
                </Table.Td>
                <Table.Td>{formatDateTime(webhook.received_at)}</Table.Td>
                <Table.Td>{when(webhook.processed_at)}</Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}
